/**
 * 代码选区管理 Hook
 *
 * 职责：
 * - 维护当前编辑器中选中代码的状态
 * - 提供清除选区的方法
 * - 拼接"提示词 + 选中代码"的发送内容
 * 
 * 设计要点：
 * - 使用 ref 同步最新选区，避免 useCallback 闭包捕获旧值
 * - hasSelection 从 ref 实时读取，确保 handleBeforeSend 调用时拿到最新状态
 */

import { useState, useRef, useCallback } from 'react';
import type { CodeSelection, PlcLanguageType } from '../types';

// ============================================
// 类型定义
// ============================================

export interface UseCodeSelectionReturn {
  /** 当前选区（null 表示无选中） */
  readonly selection: CodeSelection | null;
  /** 更新选区 */
  readonly setSelection: (sel: CodeSelection | null) => void;
  /** 清除选区 */
  readonly clearSelection: () => void;
  /** 将用户提示词与选中代码拼接为发送内容 */
  readonly buildPromptWithCode: (userPrompt: string) => string;
  /** 是否有有效选区 */
  readonly hasSelection: boolean;
}

// ============================================
// 语言名映射
// ============================================

const LANG_NAMES: Readonly<Record<PlcLanguageType, string>> = {
  st: '结构化文本 (ST)',
  il: '指令表 (IL)',
  lad: '梯形图 (LAD)',
  fbd: '功能块图 (FBD)'
};

// ============================================
// Hook 实现
// ============================================

export const useCodeSelection = (): UseCodeSelectionReturn => {
  const [selection, setSelectionState] = useState<CodeSelection | null>(null);

  // ✨ 关键修复：用 ref 同步最新选区，让 buildPromptWithCode 等回调
  //    在被调用时能拿到最新值，而不是被 useCallback 闭包捕获的旧值
  const selectionRef = useRef<CodeSelection | null>(null);

  const setSelection = useCallback((sel: CodeSelection | null): void => {
    // 过滤空选区（空字符串或纯空白）
    if (sel && !sel.code.trim()) {
      setSelectionState(null);
      selectionRef.current = null;
      return;
    }
    setSelectionState(sel);
    selectionRef.current = sel; // 同步最新选区到 ref
  }, []);

  const clearSelection = useCallback((): void => {
    setSelectionState(null);
    selectionRef.current = null;
  }, []);

  // ✨ hasSelection 仍从 state 计算（用于触发渲染，如标签显示/隐藏）
  const hasSelection = selection !== null && selection.code.trim().length > 0;

  /**
   * 拼接用户提示词和选中的代码
   * 
   * ✨ 关键：从 ref 读取选区，确保在 handleBeforeSend 调用时
   *    拿到的是最新值，而非 useCallback 捕获的旧闭包值
   *
   * 输出格式：
   * ```
   * 以下是我选中的 PLC 代码（结构化文本 (ST)，第 1-50 行）：
   * ```st
   * <代码>
   * ```
   *
   * 我的需求：<用户提示词>
   * ```
   */
  const buildPromptWithCode = useCallback(
    (userPrompt: string): string => {
      const currentSelection = selectionRef.current;

      if (!currentSelection || !currentSelection.code.trim()) {
        return userPrompt;
      }

      const langName = LANG_NAMES[currentSelection.language] ?? currentSelection.language;
      const lineRange = currentSelection.isFullSelection
        ? '全部代码'
        : `第 ${currentSelection.startLine}-${currentSelection.endLine} 行`;

      const parts: readonly string[] = [
        `以下是我选中的 PLC 代码（${langName}，${lineRange}）：`,
        '```' + currentSelection.language,
        currentSelection.code,
        '```',
        '',
        `我的需求：${userPrompt}`
      ];

      return parts.join('\n');
    },
    [] // ✨ 无依赖：通过 ref 读取最新值，回调引用稳定
  );

  return {
    selection,
    setSelection,
    clearSelection,
    buildPromptWithCode,
    hasSelection
  };
};

export default useCodeSelection;